import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useApplications } from '@data/hooks/api/use-applications-api/use-applications-api';

export interface ClassByEdApp {
  classId: string;
  name: string;
  hasStudentsEnrolled: boolean;
}

export type ClassType = 'CLASS' | 'SECTION' | 'GROUP';

export interface UseGetClassesByEdAppApiParams {
  orgId: string;
  userId: string;
  edApp: string;
  classType?: ClassType;
}

interface RawClassListItem {
  id: string;
  name: string;
}

interface UserEnrollment {
  user_id: string;
  is_primary_teacher: boolean;
}

interface RawClassDetail {
  id: string;
  name: string;
  user_enrollments?: UserEnrollment[];
}

async function getClassesByEdApp({
  orgId,
  userId,
  classType,
  productCodes,
}: {
  orgId: string;
  userId: string;
  classType: ClassType;
  productCodes: string[];
}): Promise<RawClassListItem[]> {
  return fetcher<RawClassListItem[]>(
    `/classes/orgs/${orgId}/users/${userId}/class-types/${classType}`,
    {
      service: '/roster',
      version: '/v3',
      queryParameters: { productCodes },
    }
  );
}

async function getClassDetail(classId: string): Promise<RawClassDetail> {
  return fetcher<RawClassDetail>(`/classes/${classId}`, {
    service: '/roster',
    version: '/v3',
  });
}

async function getClassesWithEnrollment({
  orgId,
  userId,
  classType,
  productCodes,
}: {
  orgId: string;
  userId: string;
  classType: ClassType;
  productCodes: string[];
}): Promise<ClassByEdApp[]> {
  const classes = await getClassesByEdApp({
    orgId,
    userId,
    classType,
    productCodes,
  });

  const details = await Promise.all(
    classes.map((cls) => getClassDetail(cls.id))
  );

  return classes.map((cls, index) => {
    const enrollments = details[index]?.user_enrollments ?? [];
    const hasStudentsEnrolled = enrollments.some(
      (enrollment) => enrollment.is_primary_teacher === false
    );
    return {
      classId: cls.id,
      name: cls.name,
      hasStudentsEnrolled,
    };
  });
}

export function useGetClassesByEdAppApi(
  { orgId, userId, edApp, classType = 'CLASS' }: UseGetClassesByEdAppApiParams,
  customQueryOptions?: Partial<UseQueryOptions<ClassByEdApp[]>>
) {
  const { data: applications } = useApplications();

  const productCodes =
    applications
      ?.find((app) => app.applicationCode === edApp)
      ?.products.map((product) => product.productCode) ?? [];

  const { enabled = true } = customQueryOptions ?? {};

  return useQuery<ClassByEdApp[]>({
    queryKey: [
      'classes-by-ed-app',
      orgId,
      userId,
      classType,
      edApp,
      productCodes,
    ],
    queryFn: () =>
      getClassesWithEnrollment({ orgId, userId, classType, productCodes }),
    enabled:
      enabled && Boolean(orgId && userId && edApp) && productCodes.length > 0,
    ...customQueryOptions,
  });
}
